import { Controller } from "@hotwired/stimulus";
import { debounce } from "lodash-es";

export default class extends Controller {
  static values = {
    presetTopic: String,
    hashtagPlaceholder: String,
    storyPlaceholder: String,
    userPlaceholder: String,
    storiesUrl: String,
    usersUrl: String,
  };
  static targets = [
    "topicTag",
    "topicOption",
    "topicsButton",
    "hashtagList",
    "hashtagTemplate",
    "hashtagInput",
    "hashtagsButton",
    "storiesList",
    "storiesButton",
    "storiesInput",
    "storiesTemplate",
    "usersButton",
    "usersList",
    "usersInput",
    "usersTemplate",
  ];

  connect() {
    if (this.hasPresetTopicValue) {
      this.enableTopic(this.presetTopicValue);
    }

    this.debouncedUpdateHashtagOptions = debounce(this.updateHashtagOptions, 400);
    this.debouncedUpdateStoriesOptions = debounce(this.updateStoriesOptions, 400);
    this.debouncedUpdateUsersOptions = debounce(this.updateUsersOptions, 400);

    this.updateTopicsButton();
    this.updateStoriesOptions();
    this.updateUsersOptions();
    if (this.hasUsersListTarget && this.hasUsersButtonTarget) {
      this.updateButton(this.usersListTarget, this.usersButtonTarget);
    }
    if (this.hasHashtagListTarget && this.hasHashtagsButtonTarget) {
      this.updateButton(this.hashtagListTarget, this.hashtagsButtonTarget);
    }
    if (this.hasStoriesListTarget && this.hasStoriesButtonTarget) {
      this.updateButton(this.storiesListTarget, this.storiesButtonTarget);
    }
  }

  topicTagTargetConnected(element) {
    const slug = element.dataset.slug;
    this.topics ||= {};
    this.topics[slug] ||= {};
    this.topics[slug].tagElement = element;
  }

  topicOptionTargetConnected(element) {
    const slug = element.dataset.slug;
    this.topics ||= {};
    this.topics[slug] ||= {};
    this.topics[slug].optionElement = element;
  }

  updateOptions(type) {
    const inputTarget = this[type + "InputTarget"];
    const search_query = inputTarget.value || "";
    const url = new URL(this[type + "UrlValue"]);
    url.searchParams.set("search", search_query);

    fetch(url, { headers: { Accept: "text/vnd.turbo-stream.html" } })
      .then((response) => response.text())
      .then((html) => Turbo.renderStreamMessage(html));
  }

  enableTopic(slug) {
    if (!this.topics[slug]) {
      console.warn(`No topic present for slug '${slug}'`);
      return;
    }

    this.topics[slug].optionElement.classList.add("d-none");
    this.topics[slug].tagElement.classList.remove("d-none");
    this.topics[slug].tagElement.querySelector("input").disabled = false;

    this.updateTopicsButton();
  }

  disableTopic(slug) {
    if (!this.topics[slug]) {
      console.warn(`No topic present for slug '${slug}'`);
      return;
    }

    this.topics[slug].optionElement.classList.remove("d-none");
    this.topics[slug].tagElement.classList.add("d-none");
    this.topics[slug].tagElement.querySelector("input").disabled = true;

    this.updateTopicsButton();
  }

  addTopic(e) {
    this.enableTopic(e.currentTarget.dataset.slug);
  }

  removeTopic(e) {
    this.disableTopic(e.currentTarget.dataset.slug);
  }

  updateTopicsButton() {
    // use tags-active class if any topic tag is showing
    if (this.topicTagTargets.filter((tag) => !tag.classList.contains("d-none")).length > 0) {
      this.topicsButtonTarget.classList.add("content-present");
    } else {
      this.topicsButtonTarget.classList.remove("content-present");
    }

    // hide button if no options remain
    if (
      this.topicOptionTargets.filter((option) => !option.classList.contains("d-none")).length < 1
    ) {
      this.topicsButtonTarget.classList.add("d-none");
    } else {
      this.topicsButtonTarget.classList.remove("d-none");
    }
  }

  addHashtag(e) {
    e.preventDefault();

    const value = e.currentTarget.value || e.currentTarget.dataset["slug"];

    if (!value) {
      return;
    }

    this.hashtagInputTarget.value = "";

    if (!this.hashtagListTarget.querySelector(`[data-slug="${value}"]`)) {
      this.createHashtagEntry(value);
    }
  }

  removeHashtag(e) {
    e.preventDefault();
    this.deleteHashtagEntry(e.currentTarget.dataset["slug"]);
  }

  createHashtagEntry(slug) {
    const newTag = this.hashtagTemplateTarget.content
      .querySelector(".discussion-tag")
      .cloneNode(true);

    // set slug in relevant attributes
    newTag.dataset["slug"] = slug;
    newTag.innerHTML = newTag.innerHTML.replaceAll(this.hashtagPlaceholderValue, slug);

    this.hashtagListTarget.appendChild(newTag);
    this.hashtagListTarget.classList.remove("d-none");
    this.updateButton(this.hashtagListTarget, this.hashtagsButtonTarget);
    this.updateHashtagOptions();
  }

  deleteHashtagEntry(slug) {
    const result = this.hashtagListTarget.querySelector(`.discussion-tag[data-slug="${slug}"]`);

    if (result) {
      result.remove();
    } else {
      console.log("no result found: ", result);
    }

    this.hashtagInputTarget.value = "";
    this.updateButton(this.hashtagListTarget, this.hashtagsButtonTarget);
    this.updateHashtagOptions();
  }

  focusInput(e) {
    e.currentTarget.querySelector("input").focus();
  }

  updateButton(list, button) {
    if (list.querySelector(`.discussion-tag`)) {
      button.classList.add("content-present");
    } else {
      button.classList.remove("content-present");
    }
  }

  updateHashtagOptions() {
    const active_hashtags = this.hashtagListTarget.querySelectorAll(".discussion-tag");
    const active_slugs = Array.from(active_hashtags).map((element) => element.dataset["slug"]);
    const search_query = this.hashtagInputTarget.value || "";

    fetch(
      "/discussion_hashtags?" +
        new URLSearchParams({
          exclude: active_slugs,
          search: search_query,
        }),
      { headers: { Accept: "text/vnd.turbo-stream.html" } },
    )
      .then((response) => response.text())
      .then((html) => Turbo.renderStreamMessage(html));
  }

  handleHashtagInputEvent(e) {
    // ensure user can only input lower case letters and numbers, and no more than 32 chars
    this.hashtagInputTarget.value = this.hashtagInputTarget.value
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "")
      .substring(0, 31);

    this.debouncedUpdateHashtagOptions();
  }

  handleStoriesInputEvent(e) {
    this.debouncedUpdateStoriesOptions();
  }

  updateStoriesOptions() {
    if (this.hasStoriesInputTarget) {
      this.updateOptions("stories");
    }
  }

  clickHandler(e) {
    e.preventDefault();

    const tag_type = e.target.closest(".popover").dataset.taggableType;
    const inputTarget = this[tag_type + "InputTarget"];

    inputTarget.value = "";
    this.addTag(tag_type, e);
  }

  addTag(tag_type, e) {
    var target = e.target.classList.contains("attachment-option")
      ? e.target
      : e.target.closest(".attachment-option");
    const value = target.querySelector(".attachment-title")
      ? target.querySelector(".attachment-title").innerText
      : target.innerText;
    const sgid = target.dataset["sgid"];
    const templateTarget = this[tag_type + "TemplateTarget"];
    const listTarget = this[tag_type + "ListTarget"];
    const buttonTarget = this[tag_type + "ButtonTarget"];
    const newTag = templateTarget.content.querySelector(".discussion-tag").cloneNode(true);

    target.classList.add("d-none");

    newTag.innerHTML = newTag.innerHTML.replaceAll("TAG_TITLE_PLACEHOLDER", value);
    newTag.dataset["sgid"] = sgid;
    newTag.querySelector("input").value = sgid;

    listTarget.appendChild(newTag);
    listTarget.classList.remove("d-none");
    this.updateButton(listTarget, buttonTarget);
  }

  removeStory(e) {
    this.removeTag(e, "stories");
  }

  removeUser(e) {
    this.removeTag(e, "users");
  }

  handleUsersInputEvent(e) {
    this.debouncedUpdateUsersOptions();
  }

  updateUsersOptions() {
    if (this.hasUsersInputTarget) {
      this.updateOptions("users");
    }
  }

  removeTag(e, type) {
    const inputTarget = this[type + "InputTarget"];
    const buttonTarget = this[type + "ButtonTarget"];
    const listTarget = this[type + "ListTarget"];
    const tag = e.currentTarget.closest(".discussion-tag");
    const sgid = tag.dataset["sgid"];

    if (document.querySelector(".d-none.attachment-option[data-sgid='" + sgid + "']")) {
      document
        .querySelectorAll(".d-none.attachment-option[data-sgid='" + sgid + "']")
        .forEach((option) => {
          option.classList.remove("d-none");
        });
    }

    if (tag) {
      tag.remove();
    } else {
      console.log("no result found: ", tag);
    }

    inputTarget.value = "";
    this.updateButton(listTarget, buttonTarget);
  }
}
